import { getRidersSessionIdFromCookie } from "../utils/cookies";
import { getColabServerUrl } from "../utils/getUrl";
import { Logger } from "../utils/logger";
import { sendPendingAchieves, handleWsAchieveResponse } from "../utils/pendingAchievements";
import { isSessionActiveCheck } from "../utils/isSessionActiveCheck";
import { isLocal } from "../utils/isLocal";
import { isPublicProject } from "../utils/publicProjects";


class SessionSocket {
	public ws: WebSocket | null = null;
	public editorSessionId: string | null = null;

	public retryCount = 0;
	public readonly MAX_RETRY_COUNT = 8;
	public reconnectTimer: NodeJS.Timeout | null = null;

	public closeEventCode = 0;

	private eventListeners: {
		open: ((event: Event) => void)[];
		close: ((event: CloseEvent) => void)[];
		error: ((event: Event) => void)[];
		message: ((event: MessageEvent) => void)[];
		shutdown: (() => void)[];
	} = {
			open: [],
			close: [],
			error: [],
			message: [],
			shutdown: []
		};

	constructor() {
		window.addEventListener("beforeunload", () => { this.disconnect(); });
	}

	public connect(editorSessionId: string) {
		try {
			if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
				return;
			}
			if (isPublicProject()) {
				return;
			}

			this.editorSessionId = editorSessionId;
			const ridersSessionId = getRidersSessionIdFromCookie();

			if (!ridersSessionId) {
				throw new Error("SessionSocket.ridersSessionIdMissing");
			}

			const url = this.getWsUrl(ridersSessionId, editorSessionId);
			this.ws = new WebSocket(url);

			this.ws.onopen = (event: Event) => {
				this.eventListeners.open.forEach((fnc) => fnc(event));
			};
			this.ws.onclose = (event: CloseEvent) => {
				this.eventListeners.close.forEach((fnc) => fnc(event));
			};
			this.ws.onerror = (event: Event) => {
				this.eventListeners.error.forEach((fnc) => fnc(event));
			};
			this.ws.onmessage = (event: MessageEvent) => {
				this.eventListeners.message.forEach((fnc) => fnc(event));
			};


		} catch (e) {
			this.disconnect();
			this.reconnect();
		}
	}

	public async isReconnectValid() {
		try {
			if (this.retryCount > 0) {
				// Retry process already started.
				return false;
			} else if (isLocal) {
				return false;

			} else if (!this.editorSessionId) {
				return false;

			} else if (
				this.closeEventCode === 4001			//Authentication failed. Your session may have expired. Please refresh the page
				|| this.closeEventCode === 4002		//Missing editor_session_id parameter
				|| this.closeEventCode === 4003		//Editor session not found.
				|| this.closeEventCode === 4004		//Editor session is not active. It may have been stopped.
				|| this.closeEventCode === 4005		//Editor session does not belong to your account.
				|| this.closeEventCode === 4006		//WebSocket connection closed by server.", # disconnect timeout
				|| this.closeEventCode === 4007		//Session stopped due to inactivity.
				|| this.closeEventCode === 4008		//Session stopped by administrator.
				|| this.closeEventCode === 4009		//Session stopped by API action.
			) {
				return false;

			} else if (!(await isSessionActiveCheck(this.editorSessionId))) {
				return false;

			}

			return true;

		} catch (e) {
			return true;
		}
	}

	public isShutdown() {
		if (isLocal) {
			return false;
		} else if (this.retryCount > 0) {
			return false;
		} else if (this.closeEventCode === 4009) {
			return false;
		}

		return true
	}

	public async reconnect() {
		let isReconnectValid = await this.isReconnectValid();
		if (!isReconnectValid) {
			if (this.isShutdown()) {
				this.eventListeners.shutdown.forEach((fnc) => fnc());
			}
			return;
		}

		this.retryCount++;

		const retry = () => {
			if (this.retryCount >= this.MAX_RETRY_COUNT) {
				this.eventListeners.shutdown.forEach((fnc) => fnc());
				return
			}
			const delay = Math.min(30_000, 1000 * Math.pow(2, this.retryCount));
			this.connect(this.editorSessionId!);
			this.reconnectTimer = setTimeout(retry, delay);
			this.retryCount++;
		}

		this.reconnectTimer = setTimeout(retry, 1000);

	}

	public disconnect() {
		if (this.ws) {
			this.ws.close();
			this.ws = null;
		}
	}
	public sendHeartbeat = () => {
		if (isLocal || isPublicProject()) {
			return;
		}

		const ws = this.ws;

		if (!ws || ws.readyState !== WebSocket.OPEN) {
			this.reconnect();
			return;
		}

		const payload = {
			type: "heartbeat",
			timestamp: new Date().toISOString(),
		};

		try {
			ws.send(JSON.stringify(payload));
			Logger.log("[EditorSessionWS] heartbeat sent", payload);
		} catch {
			Logger.log("[EditorSessionWS] heartbeat send failed");
		}
	};

	public registerEventListeners(type: "open" | "close" | "error" | "message" | "shutdown", fnc: (event?: Event | CloseEvent | MessageEvent) => void) {
		switch (type) {
			case "open":
				this.eventListeners.open.push(fnc);
				break;
			case "close":
				this.eventListeners.close.push(fnc);
				break;
			case "error":
				this.eventListeners.error.push(fnc);
				break;
			case "message":
				this.eventListeners.message.push(fnc);
				break;
			case "shutdown":
				this.eventListeners.shutdown.push(fnc);
				break;
		}
	}

	public removeEventListeners(type: "open" | "close" | "error" | "message" | "shutdown") {
		switch (type) {
			case "open":
				this.eventListeners.open = []
				break;
			case "close":
				this.eventListeners.close = [];
				break;
			case "error":
				this.eventListeners.error = [];
				break;
			case "message":
				this.eventListeners.message = [];
				break;
			case "shutdown":
				this.eventListeners.shutdown = [];
				break;
		}
	}

	private getWsUrl(ridersSessionId: string, editorSessionId: string) {
		const colabUrl = getColabServerUrl();
		if (isLocal) {
			return `ws://${colabUrl}/ws/editor-session/?sessionid=${encodeURIComponent(
				ridersSessionId,
			)}&editor_session_id=${encodeURIComponent(editorSessionId)}`;

		} else {
			return `wss://${colabUrl}/ws/editor-session/?sessionid=${encodeURIComponent(
				ridersSessionId,
			)}&editor_session_id=${encodeURIComponent(editorSessionId)}`;
		}
	}

}


export const sessionSocket = new SessionSocket();

sessionSocket.registerEventListeners("open", () => {
	if (!sessionSocket) return;
	Logger.log("SessionSocket.open");

	sessionSocket.retryCount = 0;
	if (sessionSocket.reconnectTimer) {
		clearTimeout(sessionSocket.reconnectTimer);
		sessionSocket.reconnectTimer = null;
	}
	if (sessionSocket.ws) {
		sendPendingAchieves(sessionSocket.ws);
	}
});

sessionSocket.registerEventListeners("close", (event) => {
	if (!sessionSocket) return;

	Logger.log("SessionSocket.close");
	sessionSocket.closeEventCode = (event as CloseEvent).code;
	console.log(`Connection closed with ${sessionSocket.closeEventCode} code`);
	sessionSocket.disconnect();
	sessionSocket.reconnect();
});
sessionSocket.registerEventListeners("error", () => {
	if (!sessionSocket) return;

	Logger.log("SessionSocket.error");
	sessionSocket.disconnect();
	sessionSocket.reconnect();
});




